import React from "react";
import { WhmSkeleton, WhmStack, WhmTypography } from "@securitize/reactjs-whm";
import { Translate } from "../../Translation/Translate";
import TranslationKeys from "../../../contexts/I18nContext/translationKeys";
import { useResolvedPath } from "react-router";
import { useGetInvestmentProfileQuery } from "../../../data/useProfile";
import { InvestmentLoadingCard } from "./InvestmentLoadingCard";
import { SuitabilityCard } from "./suitability/SuitabilityCard";
import { AccreditationCard } from "./accreditation/AccreditationCard";
import { WhmResponsiveStack } from "../../shared/WhmResponsiveStack";
import { ErrorPage } from "../../shared/ErrorPage";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../../data/dtos/profile-config.dto";
import { UserVerificationNeeded } from "../information/content-restricted-pages/UserVerificationNeeded";
import useUserInfo from "../../../hooks/useUserInfo";

export interface InvestmentProfileProps {
  additionalAccreditation: boolean;
  isPendingActionsLoading: boolean;
  permission: ProfilePermissionType;
}

export const InvestmentProfile = ({
  additionalAccreditation,
  isPendingActionsLoading,
  permission,
}: InvestmentProfileProps) => {
  const { pathname: currentPath } = useResolvedPath("");
  const { hasBdAccess } = useUserInfo();

  const investmentProfileQuery = useGetInvestmentProfileQuery();

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_NOT_SUBMITTED) {
    return (
      <UserVerificationNeeded
        isVerificationInReview={false}
        currentPath={currentPath}
      />
    );
  }

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_PENDING) {
    return (
      <UserVerificationNeeded
        isVerificationInReview
        currentPath={currentPath}
      />
    );
  }

  // Main UI
  if (investmentProfileQuery.isPending || isPendingActionsLoading)
    return (
      <>
        <WhmSkeleton
          data-test-id="profile-investment-profile-top-skeleton"
          variant="rounded"
          width={"100%"}
          height={52}
        />
        <WhmResponsiveStack
          gap="20px"
          sx={{
            alignItems: "baseline",
          }}
        >
          <InvestmentLoadingCard />
          <InvestmentLoadingCard />
        </WhmResponsiveStack>
      </>
    );

  if (investmentProfileQuery.error)
    return (
      <ErrorPage
        errorCode={investmentProfileQuery.error.correlationId}
        onRefresh={investmentProfileQuery.refetch}
      />
    );

  return (
    <WhmStack>
      <WhmTypography variant="h4">
        <Translate
          translationKey={TranslationKeys.PROFILE_INVESTMENT_PROFILE_TITLE}
        />
      </WhmTypography>
      <WhmTypography variant="body2" color="GrayText">
        <Translate
          translationKey={TranslationKeys.PROFILE_INVESTMENT_PROFILE_SUBTITLE}
        />
      </WhmTypography>

      <WhmResponsiveStack
        gap="20px"
        sx={{
          alignItems: "baseline",
          marginTop: "20px",
        }}
      >
        {hasBdAccess && investmentProfileQuery.data.isAllowedBrokerDealer && (
          <SuitabilityCard
            investmentProfileData={investmentProfileQuery.data}
            currentPath={currentPath}
          />
        )}

        <AccreditationCard
          additionalAccreditation={additionalAccreditation}
          investmentProfileData={investmentProfileQuery.data}
          currentPath={currentPath}
        />
      </WhmResponsiveStack>
    </WhmStack>
  );
};
